import { useEffect, useState } from "react";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { API } from "../lib/api";

/**
 * Lightweight markdown → HTML renderer (no deps). Handles the subset used by the manual:
 * headings (#–####), bold, inline code, links, ordered/unordered lists, tables, blockquotes,
 * horizontal rules, paragraphs. Good enough for our own manual.
 */
function renderMarkdown(src) {
    const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const inline = (s) =>
        escape(s)
            .replace(/`([^`]+)`/g, "<code>$1</code>")
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, src) => {
                const url = /^https?:\/\//.test(src) || src.startsWith("/") ? src : src;
                const abs = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                return `<img src="${abs}" alt="${alt}" class="manual-img" loading="lazy" />`;
            })
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    const lines = src.split("\n");
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (/^#{1,4}\s/.test(line)) {
            const level = line.match(/^#+/)[0].length;
            out.push(`<h${level}>${inline(line.replace(/^#+\s/, ""))}</h${level}>`);
            i++;
        } else if (/^\s*---+\s*$/.test(line)) {
            out.push("<hr/>");
            i++;
        } else if (/^>\s/.test(line)) {
            const buf = [];
            while (i < lines.length && /^>\s?/.test(lines[i])) {
                buf.push(inline(lines[i].replace(/^>\s?/, "")));
                i++;
            }
            out.push(`<blockquote>${buf.join("<br/>")}</blockquote>`);
        } else if (/^\|/.test(line)) {
            const rows = [];
            while (i < lines.length && /^\|/.test(lines[i])) {
                rows.push(lines[i]);
                i++;
            }
            if (rows.length >= 2) {
                const head = rows[0].split("|").slice(1, -1).map((c) => `<th>${inline(c.trim())}</th>`).join("");
                const body = rows.slice(2).map((r) => {
                    const cells = r.split("|").slice(1, -1).map((c) => `<td>${inline(c.trim())}</td>`).join("");
                    return `<tr>${cells}</tr>`;
                }).join("");
                out.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
            }
        } else if (/^\s*[-*]\s/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
                items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s/, ""))}</li>`);
                i++;
            }
            out.push(`<ul>${items.join("")}</ul>`);
        } else if (/^\s*\d+\.\s/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
                items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s/, ""))}</li>`);
                i++;
            }
            out.push(`<ol>${items.join("")}</ol>`);
        } else if (line.trim() === "") {
            i++;
        } else {
            const rendered = inline(line);
            // Don't wrap stand-alone images in <p> (invalid HTML, causes React warnings)
            if (/^<img /.test(rendered.trim())) {
                out.push(rendered);
            } else {
                out.push(`<p>${rendered}</p>`);
            }
            i++;
        }
    }
    return out.join("\n");
}

export default function HelpPage() {
    const [md, setMd] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/help/manual.md`)
            .then((r) => r.text())
            .then((text) => { setMd(text); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    return (
        <div className="bg-black text-white min-h-screen">
            <SEO title="Operational Manual" description="MyStreetzMag operational manual — how to run the platform." />
            <Header />
            <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-8 no-print">
                    <Link to="/" data-testid="help-back-link" className="inline-flex items-center gap-2 overline text-streetz-muted hover:text-streetz-red">
                        <ArrowLeft className="w-4 h-4" /> BACK
                    </Link>
                    <div className="flex gap-2">
                        <a
                            href={`${API}/help/manual.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="help-download-pdf"
                            className="inline-flex items-center gap-2 overline bg-streetz-red text-white px-4 py-2 hover:bg-white hover:text-black"
                        >
                            <Download className="w-4 h-4" /> DOWNLOAD PDF
                        </a>
                        <button
                            onClick={() => window.print()}
                            data-testid="help-print-button"
                            className="inline-flex items-center gap-2 overline border border-streetz-line px-4 py-2 hover:border-streetz-yellow text-white"
                        >
                            <Printer className="w-4 h-4" /> PRINT
                        </button>
                    </div>
                </div>

                <article
                    data-testid="help-article"
                    className="help-doc text-white/90 leading-relaxed"
                    dangerouslySetInnerHTML={{
                        __html: loading
                            ? "<p>Loading manual…</p>"
                            : DOMPurify.sanitize(renderMarkdown(md), {
                                  ADD_ATTR: ["target", "rel", "loading"],
                              }),
                    }}
                />
            </main>
            <Footer />

            <style>{`
                .help-doc h1 { font-family: 'Anton', Impact, sans-serif; font-size: clamp(2.5rem, 4vw, 4rem); color: #fff; text-transform: uppercase; letter-spacing: -0.01em; margin: 0 0 .75rem; line-height: 1; }
                .help-doc h2 { font-family: 'Anton', Impact, sans-serif; font-size: clamp(1.5rem, 2.5vw, 2.2rem); color: #fff; text-transform: uppercase; margin: 2.5rem 0 1rem; padding-left: 12px; border-left: 4px solid #FFCC00; }
                .help-doc h3 { font-family: 'Anton', Impact, sans-serif; font-size: 1.3rem; color: #FFCC00; text-transform: uppercase; margin: 1.5rem 0 .5rem; }
                .help-doc p { margin: .6rem 0; }
                .help-doc ul, .help-doc ol { padding-left: 1.6rem; margin: .6rem 0; }
                .help-doc li { margin: .25rem 0; }
                .help-doc code { background: #1A1A1A; color: #FFCC00; padding: 2px 6px; border: 1px solid #262626; font-family: 'JetBrains Mono', monospace; font-size: .85em; }
                .help-doc table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: .9rem; }
                .help-doc th { background: #1A1A1A; color: #FFCC00; text-align: left; padding: 8px 12px; text-transform: uppercase; font-size: .75rem; letter-spacing: .1em; border: 1px solid #262626; }
                .help-doc td { padding: 8px 12px; border: 1px solid #262626; vertical-align: top; }
                .help-doc tr:nth-child(even) td { background: #0a0a0a; }
                .help-doc hr { border: 0; border-top: 1px solid #262626; margin: 1.5rem 0; }
                .help-doc blockquote { border-left: 4px solid #E11D2A; background: #110000; padding: .75rem 1rem; margin: 1rem 0; color: #fff; }
                .help-doc a { color: #FFCC00; text-decoration: underline; }
                .help-doc strong { color: #fff; }
                .help-doc img.manual-img { display: block; width: 100%; max-width: 920px; margin: 1.2rem auto; border: 1px solid #262626; background: #0a0a0a; }
                .help-doc p:has(img.manual-img) { margin: 0; }
                @media print {
                    .no-print { display: none !important; }
                    body { background: #fff !important; color: #000 !important; }
                    [data-testid="site-header"], [data-testid="site-footer"] { display: none !important; }
                    .help-doc { color: #000 !important; }
                    .help-doc h1, .help-doc h2, .help-doc h3 { color: #000 !important; }
                    .help-doc code, .help-doc th { color: #c0392b !important; background: #f4f4f4 !important; }
                    .help-doc a { color: #c0392b !important; }
                }
            `}</style>
        </div>
    );
}
